import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import { enableTerminalPairing, openKioskSetupPage, startKioskTestTransaction } from 'api/Kiosk';

import { socketOff, socketOn } from 'lib/clients/socket';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Button/Button';

const resultClass = (kind) =>
  kind === 'success' ? 'bg-success-50 text-success-700' : 'bg-danger-50 text-danger-700';

// Dedicated "guided setup" panel: send a kiosk to its validation page, pair the
// terminal, and run a patient-less $0.01 test charge (auto-voided server-side). The
// result arrives on the kiosk staff room the edit modal already joined.
const KioskSetupActions = ({ kiosk }) => {
  const navigate = useNavigate();
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  const handlePaymentUpdate = useCallback(
    (data) => {
      if (data?.kioskId && kiosk?.id && data.kioskId !== kiosk.id) return;
      if (!data?.kind) return;
      setIsTesting(false);
      setTestResult({ kind: data.kind, message: data.message || 'Test transaction finished.' });
    },
    [kiosk?.id]
  );

  useEffect(() => {
    socketOn('kiosk-payment-update', handlePaymentUpdate);
    return () => socketOff('kiosk-payment-update', handlePaymentUpdate);
  }, [handlePaymentUpdate]);

  // Safety net: the result normally arrives over the socket, but if the kiosk
  // never responds, stop showing the in-progress state after the reader times out.
  useEffect(() => {
    if (!isTesting) return undefined;
    const timer = setTimeout(() => setIsTesting(false), 70000);
    return () => clearTimeout(timer);
  }, [isTesting]);

  const { mutate: openSetupPage } = useCustomMutation({
    mutationFn: () => openKioskSetupPage(navigate, { kioskId: kiosk?.id }),
    successMessage: 'Kiosk sent to the setup page',
    errorMessage: 'There was a problem opening the setup page'
  });

  const { mutate: pairTerminal } = useCustomMutation({
    mutationFn: () => enableTerminalPairing(navigate, { kioskId: kiosk?.id }),
    successMessage: 'Terminal pairing enabled',
    errorMessage: 'There was a problem enabling terminal pairing'
  });

  const { mutate: runTestCharge } = useCustomMutation({
    mutationFn: () => startKioskTestTransaction(navigate, { kioskId: kiosk?.id }),
    successMessage: 'Test charge started — follow the reader on the kiosk',
    errorMessage: 'There was a problem starting the test charge'
  });

  // The final result arrives over the socket; only clear the in-progress state
  // here if the request itself failed to start the charge.
  const handleRunTest = () => {
    setTestResult(null);
    setIsTesting(true);
    runTestCharge(undefined, {
      onSuccess: (data) => {
        if (data?.code !== 0) setIsTesting(false);
      },
      onError: () => setIsTesting(false)
    });
  };

  return (
    <div className="flex flex-col gap-3 px-5">
      <div className="flex flex-col gap-1">
        <p className="font-500 text-neutral-900">Kiosk setup</p>
        <p className="text-sm text-neutral-700">
          Send this kiosk to its setup page to grant camera/microphone access, pair the terminal,
          and run a $1 test charge that is automatically voided.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="tertiary"
          size="small"
          text="Open setup page on kiosk"
          onClick={openSetupPage}
          icon="settings-mono"
          iconColor="neutral"
        />
        <Button
          variant="tertiary"
          size="small"
          text="Pair terminal"
          onClick={pairTerminal}
          icon="tick-circle-mono"
          iconColor="neutral"
        />
        <Button
          size="small"
          color="primary"
          text={isTesting ? 'Test in progress…' : 'Run $1 test charge'}
          isDisabled={isTesting}
          onClick={handleRunTest}
          icon="credit-card-mono"
        />
      </div>
      {testResult && (
        <span
          className={`w-fit rounded-full !px-3 !py-1 text-sm font-600 ${resultClass(testResult.kind)}`}>
          {testResult.message}
        </span>
      )}
    </div>
  );
};

export default KioskSetupActions;
